<template>
  <div class="canvasChart">
    <v-chart :option="chartOption" autoresize style="height: 300px" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([
  BarChart,
  LineChart,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  CanvasRenderer,
])

const PALETTE = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4',
  '#f97316', '#ec4899', '#14b8a6', '#a855f7', '#64748b', '#84cc16',
]

const props = defineProps<{
  rows: Record<string, any>[]
  displayConfig: {
    chart_type?: 'bar' | 'line' | 'pie'
    chart_x?: string
    chart_y?: string
    group_by_column?: string
  }
}>()

const chartOption = computed(() => {
  const { chart_type = 'bar', chart_x, chart_y, group_by_column } = props.displayConfig
  if (!chart_x || !chart_y) {
    return { title: { text: 'Missing chart configuration', left: 'center', top: 'center' } }
  }

  // ── Pie ──
  if (chart_type === 'pie') {
    return {
      color: PALETTE,
      tooltip: { trigger: 'item' },
      legend: { bottom: 0, type: 'scroll', textStyle: { fontSize: 11 } },
      series: [{
        type: 'pie',
        radius: ['30%', '65%'],
        bottom: 30,
        data: props.rows.map(r => ({
          name: String(r[chart_x] ?? ''),
          value: Number(r[chart_y]) || 0,
        })),
        label: { show: true, formatter: '{b}: {c} ({d}%)', fontSize: 11 },
        emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' } },
      }],
    }
  }

  // ── Multi-series (grouped) bar / line ──
  if (group_by_column) {
    // Dedupe x-axis categories in order of appearance.
    const catSet = new Set<string>()
    for (const r of props.rows) catSet.add(String(r[chart_x] ?? ''))
    const categories = [...catSet]

    // Dedupe series names in order of appearance.
    const seriesSet = new Set<string>()
    for (const r of props.rows) seriesSet.add(String(r[group_by_column] ?? ''))
    const seriesNames = [...seriesSet]

    // Build a lookup: seriesName -> category -> value
    const lookup = new Map<string, Map<string, number>>()
    for (const name of seriesNames) lookup.set(name, new Map())
    for (const r of props.rows) {
      const sName = String(r[group_by_column] ?? '')
      const cat = String(r[chart_x] ?? '')
      const val = Number(r[chart_y]) || 0
      lookup.get(sName)!.set(cat, val)
    }

    const series = seriesNames.map((name, i) => ({
      name,
      type: chart_type as string,
      data: categories.map(cat => lookup.get(name)!.get(cat) ?? 0),
      label: {
        show: true,
        position: chart_type === 'bar' ? 'insideTop' as const : 'top' as const,
        fontSize: 13,
        fontWeight: 600,
        color: chart_type === 'bar' ? '#fff' : '#666',
        formatter: (p: any) => p.value === 0 ? '' : p.value,
      },
      itemStyle: {
        color: PALETTE[i % PALETTE.length],
        borderRadius: chart_type === 'bar' ? [4, 4, 0, 0] : undefined,
      },
      ...(chart_type === 'line' ? {
        lineStyle: { width: 2 },
        showSymbol: false,
      } : {}),
    }))

    return {
      color: PALETTE,
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0, type: 'scroll', textStyle: { fontSize: 11 } },
      grid: { left: 60, right: 20, top: 20, bottom: 40 + Math.ceil(seriesNames.length / 4) * 18 },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { rotate: categories.length > 8 ? 30 : 0, fontSize: 11 },
      },
      yAxis: { type: 'value' },
      series,
    }
  }

  // ── Single-series bar / line ──
  const categories = props.rows.map(r => String(r[chart_x] ?? ''))
  const values = props.rows.map(r => Number(r[chart_y]) || 0)

  const coloredData = values.map((v, i) => ({
    value: v,
    itemStyle: { color: PALETTE[i % PALETTE.length] },
  }))

  return {
    color: PALETTE,
    tooltip: { trigger: 'axis' },
    grid: { left: 60, right: 20, top: 30, bottom: 40 },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { rotate: categories.length > 8 ? 30 : 0, fontSize: 11 },
    },
    yAxis: { type: 'value' },
    series: [{
      type: chart_type,
      data: chart_type === 'bar' ? coloredData : values,
      label: {
        show: true,
        position: chart_type === 'bar' ? 'insideTop' as const : 'top' as const,
        fontSize: 13,
        fontWeight: 600,
        color: chart_type === 'bar' ? '#fff' : '#666',
        formatter: (p: any) => p.value === 0 ? '' : p.value,
      },
      itemStyle: { borderRadius: chart_type === 'bar' ? [4, 4, 0, 0] : undefined },
      lineStyle: chart_type === 'line' ? { color: PALETTE[0], width: 2 } : undefined,
      showSymbol: chart_type === 'line' ? false : undefined,
    }],
  }
})
</script>

<style scoped>
.canvasChart {
  padding: 8px;
}
</style>
